import type {
  SessionChargeDetailResponseDto,
  ListSessionChargesResponseDto,
  ListSessionTransitionsResponseDto,
  ListSessionProblemsResponseDto,
  ListSessionReadingsResponseDto,
} from '@evimo/types/api/session/session.response.dto';
import { buildApiUrl } from '~/app/lib/api-config';

export type ListChargesParams = {
  page: number;
  limit: number;
  q?: string;
  siteName?: string;
  chargerName?: string;
  connectorName?: string;
  sessionStatus?: string;
  anomalyQualification?: string;
  from?: string;
  to?: string;
};

async function getJson<T>(path: string): Promise<T> {
  const response = await fetch(buildApiUrl(path), {
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Session request failed (${response.status})`);
  }
  return (await response.json()) as T;
}

export const SessionService = {
  listCharges(
    params: ListChargesParams,
  ): Promise<ListSessionChargesResponseDto> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('limit', String(params.limit));
    if (params.q) search.set('q', params.q);
    if (params.siteName) search.set('siteName', params.siteName);
    if (params.chargerName) search.set('chargerName', params.chargerName);
    if (params.connectorName) search.set('connectorName', params.connectorName);
    if (params.sessionStatus) search.set('sessionStatus', params.sessionStatus);
    if (params.anomalyQualification) {
      search.set('anomalyQualification', params.anomalyQualification);
    }
    if (params.from) search.set('from', params.from);
    if (params.to) search.set('to', params.to);
    return getJson(`/api/v1/session/charges?${search.toString()}`);
  },

  getCharge(chargeId: string): Promise<SessionChargeDetailResponseDto> {
    return getJson(`/api/v1/session/charges/${chargeId}`);
  },

  getChargeTransitions(
    chargeId: string,
    params: { page: number; limit: number },
  ): Promise<ListSessionTransitionsResponseDto> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('limit', String(params.limit));
    return getJson(
      `/api/v1/session/charges/${chargeId}/transitions?${search.toString()}`,
    );
  },

  getChargeProblems(
    chargeId: string,
    params: { page: number; limit: number },
  ): Promise<ListSessionProblemsResponseDto> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('limit', String(params.limit));
    return getJson(
      `/api/v1/session/charges/${chargeId}/problems?${search.toString()}`,
    );
  },

  getChargeReadings(
    chargeId: string,
    params: { page: number; limit: number },
  ): Promise<ListSessionReadingsResponseDto> {
    const search = new URLSearchParams();
    search.set('page', String(params.page));
    search.set('limit', String(params.limit));
    return getJson(
      `/api/v1/session/charges/${chargeId}/readings?${search.toString()}`,
    );
  },
};
